import type { LucideIcon } from "lucide-react";
import { Rocket, CreditCard, ShieldCheck, Smartphone, MessageCircle } from "lucide-react";
import { Container } from "../components/ui";
import { FaqAccordion, PromoBanner, Button as DSButton, PageHeading, SectionHeading } from "../ds/core";
import { useLocale } from "../i18n/useLocale";
import type { TFunction } from "i18next";
import { TRIAL_PATH as TRIAL } from "../config/entrypoints";

const GRID_W = {
  backgroundImage:
    "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
  backgroundSize: "64px 64px",
  maskImage: "linear-gradient(to bottom, black, transparent)",
  WebkitMaskImage: "linear-gradient(to bottom, black, transparent)",
} as const;

type Group = { id: string; group: string; icon: LucideIcon; items: { q: string; a: string }[] };

function gettingStarted(t: TFunction): Group {
  return {
    id: "getting-started", group: t("faq:group.gettingStarted"), icon: Rocket,
    items: [
      { q: t("faq:gettingStarted.q1"), a: t("faq:gettingStarted.a1") },
      { q: t("faq:gettingStarted.q2"), a: t("faq:gettingStarted.a2") },
      { q: t("faq:gettingStarted.q3"), a: t("faq:gettingStarted.a3") },
      { q: t("faq:gettingStarted.q4"), a: t("faq:gettingStarted.a4") },
      { q: t("faq:gettingStarted.q5"), a: t("faq:gettingStarted.a5") },
    ],
  };
}

function pricing(t: TFunction): Group {
  return {
    id: "pricing", group: t("faq:group.pricing"), icon: CreditCard,
    items: [
      { q: t("faq:pricing.q1"), a: t("faq:pricing.a1") },
      { q: t("faq:pricing.q2"), a: t("faq:pricing.a2") },
      { q: t("faq:pricing.q3"), a: t("faq:pricing.a3") },
      { q: t("faq:pricing.q4"), a: t("faq:pricing.a4") },
    ],
  };
}

function complianceBelgium(t: TFunction): Group {
  return {
    id: "compliance", group: t("faq:group.compliance.be"), icon: ShieldCheck,
    items: [
      { q: t("faq:compliance.be.q1"), a: t("faq:compliance.be.a1") },
      { q: t("faq:compliance.be.q2"), a: t("faq:compliance.be.a2") },
      { q: t("faq:compliance.be.q3"), a: t("faq:compliance.be.a3") },
      { q: t("faq:compliance.be.q4"), a: t("faq:compliance.be.a4") },
    ],
  };
}

function complianceInternational(t: TFunction): Group {
  return {
    id: "compliance", group: t("faq:group.compliance.intl"), icon: ShieldCheck,
    items: [
      { q: t("faq:compliance.intl.q1"), a: t("faq:compliance.intl.a1") },
      { q: t("faq:compliance.intl.q2"), a: t("faq:compliance.intl.a2") },
      { q: t("faq:compliance.intl.q3"), a: t("faq:compliance.intl.a3") },
      { q: t("faq:compliance.intl.q4"), a: t("faq:compliance.intl.a4") },
    ],
  };
}

function app(t: TFunction): Group {
  return {
    id: "app", group: t("faq:group.app"), icon: Smartphone,
    items: [
      { q: t("faq:app.q1"), a: t("faq:app.a1") },
      { q: t("faq:app.q2"), a: t("faq:app.a2") },
      { q: t("faq:app.q3"), a: t("faq:app.a3") },
    ],
  };
}

export default function Faq() {
  const { market, t } = useLocale();
  const groups: Group[] = [
    gettingStarted(t),
    pricing(t),
    market === "belgium" ? complianceBelgium(t) : complianceInternational(t),
    app(t),
  ];

  return (
    <>
      {/* 1 ── Blue hero */}
      <PageHeading
        eyebrow={t("faq:hero.eyebrow")}
        title={t("faq:hero.title")}
        intro={t("faq:hero.intro")}
      />

      {/* 2 ── Topic heading + contact (left) · grouped accordions (right) */}
      <section className="bg-bg font-ds">
        <Container className="py-16 md:py-20">
          <div className="grid gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16">
            {/* Left — heading + contact card (sticky), no topic nav */}
            <div className="lg:sticky lg:top-24 lg:self-start">
              <SectionHeading eyebrow={t("faq:browse.eyebrow")} title={t("faq:browse.title")} />
              <p className="mt-4 text-ds-18 leading-relaxed text-ds-ink-soft">
                {t("faq:browse.lead")}
              </p>

              <div className="relative mt-8 overflow-hidden rounded-3xl bg-ds-hero p-6 md:p-7">
                <div className="pointer-events-none absolute inset-0" style={GRID_W} />
                <div className="relative">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-white">
                    <MessageCircle size={20} strokeWidth={2} />
                  </span>
                  <h3 className="mt-4 font-display text-ds-22 font-semibold tracking-tight text-white">{t("faq:stuck.title")}</h3>
                  <p className="mt-2 text-ds-14 leading-relaxed text-white/70">
                    {t("faq:stuck.body")}
                  </p>
                  <div className="mt-5">
                    <DSButton to="/contact" variant="solid-light">{t("faq:stuck.cta")}</DSButton>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — grouped accordions (no header icons) */}
            <div className="flex flex-col gap-12">
              {groups.map((g) => (
                <div key={g.id}>
                  <h3 className="font-display text-ds-22 font-semibold tracking-tight text-ds-ink">{g.group}</h3>
                  <div className="mt-5">
                    <FaqAccordion items={g.items} defaultOpen={-1} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* 3 ── Closing CTA */}
      <PromoBanner
        eyebrow={t("faq:cta.eyebrow")}
        title={t("faq:cta.title")}
        body={t("faq:cta.body")}
        action={
          <div className="flex flex-wrap items-center justify-center gap-3">
            <DSButton to={TRIAL} variant="solid-light">{t("faq:cta.startTrial")}</DSButton>
            <DSButton to="/contact" variant="ghost">{t("faq:cta.contact")}</DSButton>
          </div>
        }
      />
    </>
  );
}

